import { apiFetch } from './client';

// CommandIntent mirrors the JSON returned by the API: see internal/api/commands.go
// commandResponse. Operation args are kept as the raw JSON shape — the activity
// log only needs `type` for a friendly label, not the inner fields.
export interface OperationEnvelope {
  type: string;
  args: Record<string, unknown>;
}

export interface CommandIntent {
  id: string;
  status: 'pending' | 'executed' | 'rejected' | 'undone' | 'expired' | 'failed';
  // Actor metadata. actor_user_id is always present on user-initiated rows;
  // actor_name / actor_email come from a join to core.user and stay empty
  // for worker / system rows or when the user was hard-deleted.
  actor_user_id?: string;
  actor_name?: string;
  actor_email?: string;
  // 'user' = typed Operation flow (e.g. Members table delete), 'ai' =
  // proposed via the AI chat command bar. Drives the activity feed's
  // "Sparked by AI" badge.
  actor_kind: 'user' | 'ai';
  user_input: string;
  plan_summary: string;
  plan_impact?: string;
  operations: OperationEnvelope[];
  inverse_ops?: OperationEnvelope[];
  revertable: boolean;
  expires_at: string;
  undoable_until?: string;
  created_at: string;
  executed_at?: string;
  undone_at?: string;
  failed_reason?: string;
}

export interface ListCommandsResponse {
  items: CommandIntent[];
  total: number;
}

interface ListOpts {
  limit?: number;
  offset?: number;
}

export async function listCommands(
  token: string | null,
  slug: string,
  opts: ListOpts = {},
): Promise<ListCommandsResponse> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const path = `/api/v1/organizations/${encodeURIComponent(slug)}/commands${qs ? `?${qs}` : ''}`;
  return apiFetch<ListCommandsResponse>(path, { token });
}

export async function revertCommand(
  token: string | null,
  slug: string,
  intentID: string,
): Promise<CommandIntent> {
  return apiFetch<CommandIntent>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/commands/${encodeURIComponent(intentID)}/revert`,
    { method: 'POST', token },
  );
}

export interface CreateCommandInput {
  user_input: string;
  plan_summary: string;
  plan_impact?: string;
  operations: OperationEnvelope[];
}

// createCommand POSTs a new pending intent. Mirrors the typed CreateInput on
// the server. Pair with approveCommand for one-shot user actions (delete row,
// confirm a single change) where a separate approval UI isn't needed.
export async function createCommand(
  token: string | null,
  slug: string,
  input: CreateCommandInput,
): Promise<CommandIntent> {
  return apiFetch<CommandIntent>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/commands`,
    { method: 'POST', body: input, token },
  );
}

// Preflight verdict for a pending intent — advisory schedule-conflict check the
// assistant shows before an admin approves a (leader) proposal. Read-only; the
// authoritative gate is still Approve. v1 covers spatial double-booking.
export interface PreflightCheck {
  op_index: number;
  op_type: string;
  status: 'clean' | 'conflict';
  detail?: string;
}
export interface PreflightResult {
  blocked: boolean;
  checks: PreflightCheck[];
}

export async function preflightCommand(
  token: string | null,
  slug: string,
  intentID: string,
): Promise<PreflightResult> {
  return apiFetch<PreflightResult>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/commands/${encodeURIComponent(intentID)}/preflight`,
    { method: 'POST', token },
  );
}

export async function approveCommand(
  token: string | null,
  slug: string,
  intentID: string,
): Promise<CommandIntent> {
  return apiFetch<CommandIntent>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/commands/${encodeURIComponent(intentID)}/approve`,
    { method: 'POST', token },
  );
}

// dismissCommand records a user-initiated reject of a pending intent. The
// pending CommandIntent itself is left to expire on its 15-min TTL; this
// call exists so the AI usage log can distinguish "user rejected" from
// "user closed the tab and the intent timed out". Returns 204 on success
// — no body, idempotent on repeat calls.
export async function dismissCommand(
  token: string | null,
  slug: string,
  intentID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/commands/${encodeURIComponent(intentID)}/dismiss`,
    { method: 'POST', token },
  );
}
